(() => {
  if (customElements.get('navarambh-video-rail')) return;

  class NavarambhVideoRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.nvRail__track');
      this.cards = [...this.querySelectorAll('.nvRail__card')];
      this.dots = [...this.querySelectorAll('.nvRail__dots button')];
      if (!this.track || !this.cards.length) return;
      this.cards.forEach(card => this.prepareYouTube(card));

      this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = -1;
      this.scrollFrame = null;
      this.onScroll = () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateClosest();
        });
        clearTimeout(this.dotTimer);
        this.dotTimer = setTimeout(() => this.syncDots(this.activeIndex), 240);
      };
      this.onVisibility = () => { if (document.hidden) this.pauseAll(true); };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.onPointerDown = event => {
        if (event.pointerType !== 'mouse' || this.track.scrollWidth <= this.track.clientWidth + 2 || event.target.closest('button, a')) return;
        this.drag = { x: event.clientX, left: this.track.scrollLeft };
        this.track.classList.add('is-drag');
      };
      this.onPointerMove = event => {
        if (this.drag) this.track.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
      };
      this.onPointerUp = () => { this.drag = null; this.track.classList.remove('is-drag'); };
      this.track.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.navLockUntil = performance.now() + 850;
        this.setLead(index);
        this.center(index, true);
        clearTimeout(this.centerTimer);
        this.centerTimer = setTimeout(() => { this.center(index, false); this.updateClosest(); }, 870);
      }));
      this.cards.forEach((card, index) => {
        card.querySelector('.nvRail__play')?.addEventListener('click', () => this.toggleVideo(card));
        card.querySelector('.nvRail__sound')?.addEventListener('click', () => this.toggleSound(card));
        card.addEventListener('focusin', () => { this.setLead(index); this.center(index, true); });
        card.querySelector('video')?.addEventListener('ended', () => this.pauseCard(card));
      });

      this.revealObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.classList.add('is-in');
      }, { rootMargin: '0px 0px -10% 0px' });
      this.revealObserver.observe(this);
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => !entry.isIntersecting)) this.pauseAll(true);
      }, { threshold: 0 });
      this.visibilityObserver.observe(this);

      const first = this.isCarousel() ? 0 : Math.floor(this.cards.length / 2);
      this.setLead(first);
      requestAnimationFrame(() => {
        if (this.isCarousel()) this.track.scrollLeft = 0;
        this.classList.add('is-ready');
      });
      this.onResize = () => {
        const carousel = this.isCarousel();
        const index = carousel === this.wasCarousel ? this.activeIndex : carousel ? 0 : Math.floor(this.cards.length / 2);
        this.wasCarousel = carousel;
        this.activeIndex = -1;
        this.setLead(index);
        if (carousel) this.center(index, false);
        else this.track.scrollLeft = 0;
      };
      this.wasCarousel = this.isCarousel();
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      this.pauseAll(true);
      this.revealObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this.onResize);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.centerTimer);
      clearTimeout(this.dotTimer);
    }

    setLead(index, syncDots = true) {
      if (index < 0 || index >= this.cards.length || this.activeIndex === index) return;
      this.activeIndex = index;
      const lead = parseFloat(getComputedStyle(this).getPropertyValue('--nv-lead')) || 1.25;
      this.cards.forEach((card, i) => {
        card.classList.toggle('is-lead', i === index);
        card.style.setProperty('--nv-scale', (i === index ? lead : Math.abs(i - index) === 1 ? 1 + (lead - 1) * .5 : 1).toFixed(3));
      });
      if (syncDots) this.syncDots(index);
      this.cards.forEach((card, i) => { if (i !== index) this.pauseCard(card, true); });
    }

    syncDots(index) {
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    isCarousel() {
      return this.cards.length > 5 || matchMedia('(max-width: 1159px)').matches;
    }

    center(index, animate) {
      if (!this.isCarousel() || this.track.scrollWidth <= this.track.clientWidth + 2) return;
      const card = this.cards[index];
      if (!card) return;
      const desired = this.track.scrollLeft + card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + card.offsetWidth / 2 - this.track.clientWidth / 2;
      const bounded = Math.max(0, Math.min(desired, this.track.scrollWidth - this.track.clientWidth));
      this.track.scrollTo({ left: bounded, behavior: animate && !this.reduceMotion.matches ? 'smooth' : 'instant' });
    }

    updateClosest() {
      if (!this.isCarousel() || performance.now() < (this.navLockUntil || 0)) return;
      if (this.track.scrollLeft < 2) { this.setLead(0, false); return; }
      if (this.track.scrollLeft > this.track.scrollWidth - this.track.clientWidth - 2) { this.setLead(this.cards.length - 1, false); return; }
      const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
      let best = 0;
      let distance = Infinity;
      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { best = index; distance = delta; }
      });
      this.setLead(best, false);
    }

    toggleVideo(card) {
      if (card.dataset.kind === 'none') return;
      this.navLockUntil = performance.now() + 850;
      if (card.classList.contains('is-playing')) { this.pauseCard(card); return; }
      this.pauseAll(true);
      const index = this.cards.indexOf(card);
      this.setLead(index);
      this.center(index, true);
      if (card.dataset.kind === 'shopify') {
        const video = card.querySelector('video');
        if (!video) return;
        video.muted = card.dataset.soundOn !== 'true';
        video.preload = 'auto';
        const request = card.playRequest = (card.playRequest || 0) + 1;
        this.markPlaying(card, true);
        try {
          Promise.resolve(video.play()).then(() => {
            if (card.playRequest !== request) video.pause();
          }).catch(() => { if (card.playRequest === request) this.markPlaying(card, false); });
        } catch {
          if (card.playRequest === request) this.markPlaying(card, false);
        }
      } else if (card.dataset.kind === 'youtube') {
        if (card.querySelector('iframe')) this.youtubeCommand(card, 'playVideo');
        else this.createYouTube(card);
        this.markPlaying(card, true);
      }
    }

    parseYouTube(raw) {
      try {
        const value = raw.trim();
        const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
        const host = url.hostname.toLowerCase().replace(/^www\./, '');
        let id = '';
        if (host === 'youtu.be') id = url.pathname.split('/').filter(Boolean)[0] || '';
        else if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
          const parts = url.pathname.split('/').filter(Boolean);
          if (parts[0] === 'watch') id = url.searchParams.get('v') || '';
          else if (['shorts', 'embed', 'live'].includes(parts[0])) id = parts[1] || '';
        }
        return /^[A-Za-z0-9_-]{11}$/.test(id) ? { id, shorts: url.pathname.split('/').filter(Boolean)[0] === 'shorts' } : null;
      } catch { return null; }
    }

    prepareYouTube(card) {
      if (card.dataset.kind !== 'youtube') return;
      const parsed = this.parseYouTube(card.dataset.youtubeUrl || '');
      const poster = card.querySelector('.nvRail__poster');
      if (!parsed) {
        card.dataset.kind = 'none';
        const button = card.querySelector('.nvRail__play');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        const placeholder = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        placeholder.setAttribute('class', 'nvRail__placeholder');
        placeholder.setAttribute('viewBox', '0 0 9 16');
        poster.replaceChildren(placeholder);
        return;
      }
      const id = parsed.id;
      card.dataset.youtubeId = id;
      card.classList.toggle('is-youtube-short', parsed.shorts);
      if (poster.querySelector('img')) return;
      const image = document.createElement('img');
      image.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
      image.width = 480;
      image.height = 360;
      image.loading = 'lazy';
      image.alt = '';
      const placeholder = poster.querySelector('svg');
      image.onerror = () => { if (placeholder) poster.replaceChildren(placeholder); };
      poster.replaceChildren(image);
    }

    createYouTube(card) {
      const id = card.dataset.youtubeId;
      if (!/^[A-Za-z0-9_-]{11}$/.test(id || '')) return;
      const mount = card.querySelector('.nvRail__embed');
      const iframe = document.createElement('iframe');
      const origin = location.origin === 'null' ? '' : `&origin=${encodeURIComponent(location.origin)}`;
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&enablejsapi=1&playsinline=1&controls=${card.classList.contains('is-youtube-short') ? '1' : '0'}&rel=0&mute=${card.dataset.soundOn === 'true' ? '0' : '1'}${origin}`;
      iframe.title = card.dataset.title || 'Navarambh video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.loading = 'eager';
      iframe.setAttribute('allowfullscreen', '');
      iframe.addEventListener('load', () => {
        this.youtubeCommand(card, card.dataset.soundOn === 'true' ? 'unMute' : 'mute');
        this.youtubeCommand(card, card.classList.contains('is-playing') ? 'playVideo' : 'pauseVideo');
      });
      mount.replaceChildren(iframe);
    }

    youtubeCommand(card, command) {
      card.querySelector('iframe')?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: command, args: [] }), 'https://www.youtube-nocookie.com');
    }

    toggleSound(card) {
      const on = card.dataset.soundOn !== 'true';
      card.dataset.soundOn = on ? 'true' : 'false';
      const button = card.querySelector('.nvRail__sound');
      button?.setAttribute('aria-pressed', String(on));
      button?.setAttribute('aria-label', `${on ? 'Turn sound off' : 'Turn sound on'} for ${card.dataset.title || 'video'}`);
      const video = card.querySelector('video');
      if (video) video.muted = !on;
      if (card.dataset.kind === 'youtube') this.youtubeCommand(card, on ? 'unMute' : 'mute');
    }

    markPlaying(card, playing) {
      card.classList.toggle('is-playing', playing);
      const button = card.querySelector('.nvRail__play');
      button?.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${card.dataset.title || 'video'}`);
      const label = button?.querySelector('.nvRail__play-label');
      if (label) label.textContent = playing ? 'Pause' : 'Play';
    }

    pauseCard(card, removeEmbed = false) {
      card.playRequest = (card.playRequest || 0) + 1;
      if (!card.classList.contains('is-playing') && !card.querySelector('iframe')) return;
      card.querySelector('video')?.pause();
      if (card.dataset.kind === 'youtube') {
        this.youtubeCommand(card, 'pauseVideo');
        if (removeEmbed) card.querySelector('.nvRail__embed')?.replaceChildren();
      }
      this.markPlaying(card, false);
    }

    pauseAll(removeEmbed) {
      this.cards?.forEach(card => this.pauseCard(card, removeEmbed));
    }
  }

  customElements.define('navarambh-video-rail', NavarambhVideoRail);
})();
